import { getCookieKeyPrefix } from '@/utils/common'
import Cookies from 'js-cookie'

// 与后端的theme_cookie_key匹配
const globalThemeCookieKey = getCookieKeyPrefix() + '_ASGlobalTheme_FF117B9D62'
const localThemeCookieKey = getCookieKeyPrefix() + '_AsLocalTheme_FF117B9D62'

// 主题映射关系
const THEME_MAP = {
  light: 4,    // 浅色主题 theme_id: 4
  dark: 5,     // 深色主题 theme_id: 5
  system: 6    // 跟随系统 theme_id: 6
}

const THEME_ID_MAP = {
  4: 'light',
  5: 'dark',
  6: 'system'
}

/**
 * 获取当前主题设置
 * @returns {string} 主题模式 'light' | 'dark' | 'system'
 */
export function getTheme() {
  let theme = null
  if (document.domain !== 'localhost') {
    theme = Cookies.get(globalThemeCookieKey)
  } else {
    theme = Cookies.get(localThemeCookieKey)
  }
  
  // 如果没有设置，默认跟随系统
  return theme || 'system'
}

/**
 * 设置主题并保存到Cookie
 * @param {string} theme 主题模式 'light' | 'dark' | 'system'
 */
export function setTheme(theme) {
  if (document.domain !== 'localhost') {
    Cookies.set(globalThemeCookieKey, theme, {
      domain: '.' + process.env.VUE_APP_DOMAIN,
      path: '/'
    })
  } else {
    Cookies.set(localThemeCookieKey, theme)
  }
}

/**
 * 获取系统主题偏好
 * @returns {string} 'light' | 'dark'
 */
export function getSystemTheme() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

/**
 * 获取实际应用的主题
 * @param {string} themeMode 主题设置模式
 * @returns {string} 'light' | 'dark'
 */
export function getActualTheme(themeMode = null) {
  const currentTheme = themeMode || getTheme()
  
  if (currentTheme === 'system') {
    return getSystemTheme()
  }
  
  return currentTheme === 'dark' ? 'dark' : 'light'
}

/**
 * 应用主题到DOM
 * @param {string} actualTheme 实际主题 'light' | 'dark'
 */
export function applyTheme(actualTheme) {
  const htmlElement = document.documentElement
  
  if (actualTheme === 'dark') {
    htmlElement.classList.add('dark')
  } else {
    htmlElement.classList.remove('dark')
  }
}

/**
 * 主题切换主函数
 * @param {string} themeMode 主题模式 'light' | 'dark' | 'system'
 * @param {object} store Vuex store 实例（可选）
 */
export function switchTheme(themeMode, store = null) {
  // 1. 保存到Cookie
  setTheme(themeMode)
  
  // 2. 获取实际应用的主题
  const actualTheme = getActualTheme(themeMode)
  
  // 3. 应用到DOM
  applyTheme(actualTheme)
  
  // 4. 同步到 Vuex store
  if (store) {
    store.dispatch('theme/setThemeMode', themeMode)
    store.dispatch('theme/setActualTheme', actualTheme)
  }
  
  return actualTheme
}

/**
 * 初始化主题
 * @param {object} store Vuex store 实例（可选）
 */
export function initTheme(store = null) {
  const themeMode = getTheme()
  const actualTheme = getActualTheme(themeMode)
  applyTheme(actualTheme)
  
  // 同步到 Vuex store
  if (store) {
    store.dispatch('theme/setThemeMode', themeMode)
    store.dispatch('theme/initThemeState')
  }
  
  // 监听系统主题变化
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleSystemThemeChange = (e) => {
      const newSystemTheme = e.matches ? 'dark' : 'light'
      
      // 更新 Vuex 中的系统主题状态
      if (store) {
        store.dispatch('theme/setSystemTheme', newSystemTheme)
      }
      
      const currentThemeMode = getTheme()
      if (currentThemeMode === 'system') {
        const newActualTheme = newSystemTheme
        applyTheme(newActualTheme)
        
        // 更新 Vuex 中的实际主题状态
        if (store) {
          store.dispatch('theme/setActualTheme', newActualTheme)
        }
      }
    }
    
    // 移除可能存在的旧监听器
    if (window._themeChangeListener) {
      mediaQuery.removeListener(window._themeChangeListener)
    }
    
    // 添加新监听器
    window._themeChangeListener = handleSystemThemeChange
    mediaQuery.addListener(handleSystemThemeChange)
  }
  
  return { themeMode, actualTheme }
}

/**
 * 根据主题key获取theme_id
 * @param {string} themeKey 主题key
 * @returns {number} theme_id
 */
export function getThemeIdByKey(themeKey) {
  return THEME_MAP[themeKey] || THEME_MAP.system
}

/**
 * 根据theme_id获取主题key
 * @param {number} themeId theme_id
 * @returns {string} 主题key
 */
export function getThemeKeyById(themeId) {
  return THEME_ID_MAP[themeId] || 'system'
}

export default {
  getTheme,
  setTheme,
  getSystemTheme,
  getActualTheme,
  applyTheme,
  switchTheme,
  initTheme,
  getThemeIdByKey,
  getThemeKeyById
}